/**
 * The non-interaction test.
 *
 * Swanson's method finds a connection nobody has written down by noticing that
 * two literatures share an intermediate term and have never cited each other.
 * The discovery is the ABSENCE of a direct link, which makes this the one place
 * in the app where the interesting quantity is a count rather than a judgement —
 * so no model is asked for any of it, for the same reason `panel.ts` counts
 * agreement instead of asking how much a panel agreed.
 *
 * The naive version of this is worthless and it is worth being explicit about
 * why, because it is the version that suggests itself. Search A, search C,
 * intersect the two result sets, call disjointness a discovery: two searches over
 * a relevance-ranked API almost never overlap, so that test fires on every pair
 * and manufactures a breakthrough out of nothing. Swanson could take
 * non-interaction as evidence because MEDLINE was closed and complete. Ten web
 * results are not a literature.
 *
 * So the null here is earned rather than observed. A third search runs on the
 * CONJUNCTION of both term sets — a deliberate attempt to prove the connection
 * is already known — and only its failure, on a pass that reported no errors and
 * was not truncated, is reported as unjoined. Failing to find something you went
 * looking for is worth what stumbling over its absence is not.
 *
 * Every threshold in this file leans the same way: toward finding the link
 * ALREADY MADE. A false "already made" costs a candidate that was probably
 * spurious anyway. A false "unjoined" is the tool announcing a discovery, which
 * is the one thing the rest of this app is built never to do.
 */

import { tokenise } from './retrieve'
import { canonicalUrl } from './url'

/** Documents below this on either side are not a literature, and nothing is read
 *  off them. Deliberately above the two-or-three a thin search returns. */
const MIN_DOCS_PER_SIDE = 4

/** An intermediate must appear in at least this many documents on EACH side.
 *  One document per side is a coincidence with a term in it. */
const MIN_TERM_DOCS = 2

/**
 * A term in nearly every document of a pile is that pile's own subject heading
 * or it is noise the stoplist missed. Either way it connects nothing, because it
 * does not distinguish the documents it appears in from the ones it doesn't.
 */
const UBIQUITY_CEILING = 0.8

/**
 * Share of a side's distinctive terms a conjunction result must carry, on each
 * side, to count as addressing both.
 *
 * The titles floor is LOWER, not higher, and that is not a slip. A title is a
 * dozen words and will carry a smaller share of a term set than a full page
 * would, so holding titles to the same bar would read "this page does not
 * address both" off nothing but the page's brevity — and every such misreading
 * pushes toward announcing a discovery. Thin evidence gets a lower bar to clear
 * precisely because the error it causes is the expensive one.
 *
 * Which floor applies is a property of the DOCUMENT being scored and never of
 * the pass. A pass-level choice reintroduces the same error one level up: one
 * page with text anywhere in the two side piles would hold every titles-only
 * conjunction result to the full-text bar, and the resulting misses all land on
 * `unjoined`.
 */
export const ADDRESSES_BOTH_TEXT = 0.34
export const ADDRESSES_BOTH_TITLE = 0.25

/** Intermediates offered to the reasoning pass. Beyond this the prompt is
 *  carrying tail terms that rank below the noise. */
const MAX_INTERMEDIATES = 8

/** A two-word term is far more likely to name a concept than either of its
 *  halves, so it outranks them at equal reach. */
const BIGRAM_BONUS = 1.35

/**
 * Vocabulary that carries no discriminating power in a pile of search results.
 *
 * This list would be WRONG in `retrieve.ts`, whose own stoplist is short by
 * design because "field", "state" and "order" are load-bearing inside a technical
 * claim. The job here is different: a candidate intermediate has to be a concept
 * you could search for ON ITS OWN and get back a coherent literature. "System"
 * fails that test in every field there is, and so does "significant". Terms are
 * dropped by that criterion and no other — which is why "temperature", "energy"
 * and "pressure" are absent from this list despite being common.
 */
const RESEARCH_STOP = new Set(
  (
    'study studies research researchers analysis analyses result results data dataset method methods' +
    ' methodology approach approaches review reviews paper papers article articles journal preprint' +
    ' published publication abstract introduction conclusion conclusions discussion findings finding' +
    ' based using used use uses useful new novel recent current present presented report reported' +
    ' significant significantly associated association correlation correlated potential potentially' +
    ' role important importance however moreover furthermore additionally therefore thus overall' +
    ' first second third one two three several many various different similar same such' +
    ' high higher low lower large larger small smaller increase increased increasing decrease' +
    ' decreased decreasing level levels case cases group groups control controls cohort' +
    ' patients subjects participants sample samples trial trials experiment experimental' +
    ' system systems model models framework general specific total mean average' +
    ' show shows shown showed suggest suggests suggested indicate indicates found' +
    ' may might could would should can also well within between across during' +
    ' effect effects impact impacts factor factors issue issues problem problems' +
    ' work works working year years time times number numbers part parts way ways' +
    ' pdf doi http https www com org abstractlink fulltext'
  ).split(' '),
)

/** A document from a search, reduced to what this file reads. */
export interface LinkDoc {
  url: string
  title: string
  /** The opened page's text, where the provider returns it. Absent on a model
   *  whose search does not — see `SeenSource.text`. */
  text?: string
}

/** One pile: a search and what it returned. */
export interface LinkPile {
  docs: LinkDoc[]
  /**
   * Whether this search's null means anything.
   *
   * False when the pass reported a search failure or was truncated at its cap —
   * the same condition `SearchOutcome` already carries, because a null read off a
   * search that errored is not a null.
   */
  sound: boolean
}

export interface Intermediate {
  term: string
  /** Documents on each side carrying it. */
  aDocs: number
  cDocs: number
  /** min(share on A, share on C), times the bigram bonus. Ranks the list. */
  score: number
}

/**
 * Every reading, as a VALUE, because two readers need it at runtime.
 *
 * `persist.ts` validates a stored reading against this list and `link.test.ts`
 * enumerates it — the same reason `PROVIDERS` and `MOVE_REASON` are values. A
 * hand-copied union in the codec is precisely the drift that dropped every
 * `custom`-stamped record on read, and a sixth reading added here would repeat
 * it: stored, then silently discarded on the next load.
 *
 *   already-made    A returned document addresses both sides. Somebody has
 *                   already joined them.
 *   unsound         A search failed or was truncated, so no null read off it is
 *                   sound.
 *   thin            One side came back with too few documents to be a literature.
 *   no-intermediate The conjunction came back clean and empty, but the piles
 *                   share no term that could carry a mechanism.
 *   unjoined        The conjunction was searched for and did not turn up, and
 *                   the piles share an intermediate. The only reading worth
 *                   spending a reasoning pass on.
 */
export const LINK_READINGS = [
  'already-made',
  'unsound',
  'thin',
  'no-intermediate',
  'unjoined',
] as const

export type LinkReading = (typeof LINK_READINGS)[number]

export interface Linkage {
  reading: LinkReading
  intermediates: Intermediate[]
  /** Documents from the conjunction search that address both sides. Empty for
   *  every reading except `already-made`, where it is the evidence. */
  joinedBy: LinkDoc[]
  /** Canonical URLs returned on BOTH sides. Reported because it is worth seeing,
   *  and deliberately NOT part of the reading: two searches sharing a result is
   *  ordinary, and treating it as interaction is the naive test this file exists
   *  to avoid. */
  shared: string[]
  /**
   * Whether page text was available anywhere in the pass. `titles` reads are
   * weaker and every surface that prints one has to say so.
   *
   * A REPORT and nothing else. It deliberately drives no threshold in this file:
   * see `ADDRESSES_BOTH_TEXT` for why that choice is load-bearing.
   */
  depth: 'text' | 'titles'
  aTerms: string[]
  cTerms: string[]
  aDocs: number
  cDocs: number
}

/**
 * The terms a side is searched and matched on.
 *
 * Label, claim and evidence pointers, in that order — the claim carries the
 * proposition and the evidence pointers carry the nouns a specialist would
 * actually search, which is exactly the vocabulary that has to appear in a
 * document for it to count as addressing this side.
 */
export function distinctiveTerms(side: {
  label: string
  claim: string
  evidence?: string[]
}): string[] {
  const raw = [side.label, side.claim, ...(side.evidence ?? [])].join(' ')
  return [...new Set(tokenise(raw))].filter((t) => !RESEARCH_STOP.has(t))
}

/** Unigrams and adjacent bigrams, deduped per document.
 *
 *  Bigrams are built after stopword removal, so a sentence boundary can fuse two
 *  unrelated words into one. That survives only if the same accident recurs in
 *  MIN_TERM_DOCS documents on BOTH sides, which the reach requirement below makes
 *  vanishingly unlikely — the breadth test is what makes the cheap bigram safe. */
function candidateTerms(text: string): Set<string> {
  const words = tokenise(text).filter((w) => !RESEARCH_STOP.has(w))
  const out = new Set<string>(words)
  for (let i = 0; i + 1 < words.length; i++) out.add(`${words[i]} ${words[i + 1]}`)
  return out
}

/** What this document gives us to read: its text where we have it, its title
 *  where we don't. Never both, so a page with text is not scored twice on a
 *  title that repeats it. */
const bodyOf = (d: LinkDoc): string => d.text ?? d.title

/** Share of `terms` present in a body of text. Unweighted: there is no background
 *  corpus here to derive an IDF from, and inventing one out of the two piles
 *  being compared would penalise exactly the shared vocabulary we are looking
 *  for. The stoplist does the work an IDF would.
 *
 *  Exported for the prior-art backstop, which asks this file's own question —
 *  does a returned page address two things at once? — of a conjecture's leap and
 *  its prediction, and must answer it the same way. */
export function termCoverage(text: string, terms: readonly string[]): number {
  if (!terms.length) return 0
  const have = new Set(tokenise(text))
  let hit = 0
  for (const t of terms) if (have.has(t)) hit++
  return hit / terms.length
}

const coverage = (doc: LinkDoc, terms: string[]): number => termCoverage(bodyOf(doc), terms)

/** Documents carrying each candidate term, one vote per document. */
function reach(docs: LinkDoc[]): Map<string, number> {
  const df = new Map<string, number>()
  for (const d of docs) {
    for (const t of candidateTerms(bodyOf(d))) df.set(t, (df.get(t) ?? 0) + 1)
  }
  return df
}

function rankIntermediates(a: LinkPile, c: LinkPile, exclude: Set<string>): Intermediate[] {
  const aDf = reach(a.docs)
  const cDf = reach(c.docs)
  const found: Intermediate[] = []

  for (const [term, aN] of aDf) {
    const cN = cDf.get(term) ?? 0
    if (aN < MIN_TERM_DOCS || cN < MIN_TERM_DOCS) continue
    // A term the sides already share because it is one of their own is not an
    // intermediate — it is the thing being connected.
    if (term.split(' ').some((w) => exclude.has(w))) continue

    const aShare = aN / a.docs.length
    const cShare = cN / c.docs.length
    if (aShare > UBIQUITY_CEILING || cShare > UBIQUITY_CEILING) continue

    // min() rather than a product: a term in 90% of one pile and 10% of the other
    // scores what its weaker side is worth, so a balanced connector outranks a
    // lopsided one instead of tying with it.
    const bonus = term.includes(' ') ? BIGRAM_BONUS : 1
    found.push({ term, aDocs: aN, cDocs: cN, score: Math.min(aShare, cShare) * bonus })
  }

  found.sort((x, y) => y.score - x.score || x.term.localeCompare(y.term))

  /*
   * Drop a unigram already carried by a bigram that outranked it, so the list
   * spends its eight slots on eight concepts rather than on three concepts and
   * their halves.
   *
   * This cannot suppress a term that reaches meaningfully further than the
   * bigram, and the bound is worth writing down because the rule reads more
   * destructive than it is. A bigram is a conjunction with an adjacency
   * constraint, so its reach `b` is necessarily `<= u` for either half; and it
   * only sorts above the half when `BIGRAM_BONUS * b > u`. Together:
   *
   *     u / BIGRAM_BONUS  <  b  <=  u
   *
   * A unigram is therefore only ever swallowed by a bigram already covering at
   * least 1/BIGRAM_BONUS — about 74% — of its reach, which is exactly the case
   * where keeping it spends a slot on a near-duplicate. Raising the bonus widens
   * that window and starts eating genuinely broader terms.
   */
  const kept: Intermediate[] = []
  for (const cand of found) {
    const swallowed =
      !cand.term.includes(' ') &&
      kept.some((k) => k.term.includes(' ') && k.term.split(' ').includes(cand.term))
    if (!swallowed) kept.push(cand)
    if (kept.length >= MAX_INTERMEDIATES) break
  }
  return kept
}

/**
 * The whole test, over three piles that have already been searched.
 *
 * Nothing here calls a model or a network. `reason.ts` maps `SeenSource[]` into
 * `LinkPile`s and hands them over, which is also why this file imports nothing
 * from `engine/`.
 */
export function assess(
  a: { label: string; claim: string; evidence?: string[]; pile: LinkPile },
  c: { label: string; claim: string; evidence?: string[]; pile: LinkPile },
  conjunction: LinkPile,
): Linkage {
  const aTerms = distinctiveTerms(a)
  const cTerms = distinctiveTerms(c)

  const anyText = [...a.pile.docs, ...c.pile.docs, ...conjunction.docs].some((d) => d.text)
  const depth: Linkage['depth'] = anyText ? 'text' : 'titles'

  const joinedBy = conjunction.docs.filter((d) => {
    const floor = d.text ? ADDRESSES_BOTH_TEXT : ADDRESSES_BOTH_TITLE
    return coverage(d, aTerms) >= floor && coverage(d, cTerms) >= floor
  })

  const aKeys = new Set(a.pile.docs.map((d) => canonicalUrl(d.url)))
  const shared = [
    ...new Set(c.pile.docs.map((d) => canonicalUrl(d.url)).filter((u) => aKeys.has(u))),
  ]

  const own = new Set([...aTerms, ...cTerms])
  const intermediates = rankIntermediates(a.pile, c.pile, own)

  const base = {
    intermediates,
    shared,
    depth,
    aTerms,
    cTerms,
    aDocs: a.pile.docs.length,
    cDocs: c.pile.docs.length,
  }

  // Precedence, and the order is the argument.
  //
  // `already-made` is tested FIRST, ahead of soundness, because it is the only
  // reading here built on positive evidence: a document that addresses both sides
  // says so whatever else went wrong in the pass. Every reading below it is a
  // null, and a null inherits every doubt the pass collected — which is why
  // `unsound` sits directly beneath it and above everything else.
  if (joinedBy.length) return { ...base, reading: 'already-made', joinedBy }
  if (!a.pile.sound || !c.pile.sound || !conjunction.sound)
    return { ...base, reading: 'unsound', joinedBy: [] }
  if (a.pile.docs.length < MIN_DOCS_PER_SIDE || c.pile.docs.length < MIN_DOCS_PER_SIDE)
    return { ...base, reading: 'thin', joinedBy: [] }
  if (!intermediates.length) return { ...base, reading: 'no-intermediate', joinedBy: [] }
  return { ...base, reading: 'unjoined', joinedBy: [] }
}

/** Whether a linkage is worth spending a reasoning pass on. The one predicate
 *  the caller should branch on, so the precedence above has a single reader. */
export const worthReasoning = (l: Linkage): boolean => l.reading === 'unjoined'
